import AnimatedWrapper from "../components/AnimatedWrapper";
import { useNavigate } from "react-router-dom";
import { useUser } from "../context/useInfo";
import amplitude from "../utils/amplitude";
import { motion, useMotionValue, animate } from "framer-motion";
import { useEffect, useState } from "react";

export default function TreatmentScreen() {
  const { userData } = useUser();
  const navigate = useNavigate();

  useEffect(() => {
    if (process.env.NODE_ENV === "production") {
      amplitude.track('Treatment Program View');
    }
  }, []);

  const handleNextStep = () => {
    if (process.env.NODE_ENV === "production") {
      amplitude.track('Quiz Complete');
    }
    navigate("/keep-results");
  };

  return (
    <AnimatedWrapper>
      {/* Title */}
      <div className="text-center">
        <h2 className="text-2xl font-semibold mt-3">Treatment program with <br /> 100% fit</h2>
        <p className="text-sm text-gray-600 mt-1 mb-5">
          Let's get dream skin, we will introduce you to <br /> your personal routines
        </p>
      </div>

      {/* Image & Progress */}
      <div className="flex h-[250px] gap-4 max-w-[450px] mb-5">
        <img
          src={userData.photo || "/imgTreatment.png"}
          alt="User"
          className="h-full w-[160px] sm:w-[180px] md:w-[200px] object-cover rounded-xl bg-white"
        />
        <div className="flex-1 space-y-2">
          <Progress 
            label="Hydration" 
            finalValue={56}
            color="bg-pinkCustom" 
            delay={0.1}
          />
          <Progress 
            label="Elasticity" 
            finalValue={43}
            color="bg-green-500" 
            delay={0.3}
          />
          <Progress 
            label="Complexion" 
            finalValue={70}
            color="bg-purple-500" 
            delay={0.5}
          />
          <Progress 
            label="Texture" 
            finalValue={83}
            color="bg-orange-400" 
            delay={0.7}
          />
        </div>
      </div>

      {/* Program Description */}
      <div className="bg-white border rounded-lg p-4 space-y-2 text-sm text-gray-700 shadow-xl mb-5">
        <p>1. Based on your skin data</p>
        <p>2. Tailored for significant skin improvement</p>
        <p>3. Designed to provide long-lasting results through skincare guidance</p>
      </div>

      {/* Button */}
      <button 
        className="w-full bg-pinkCustom text-white rounded-full py-3 text-center font-semibold text-sm hover:bg-pink-600 transition" 
        onClick={() => handleNextStep()}
      >
        Next
      </button>
    </AnimatedWrapper>
  );
}

function Progress({ label, finalValue, color, delay = 0 }) {
  const [displayValue, setDisplayValue] = useState(0);
  const motionValue = useMotionValue(0);

  useEffect(() => {
    const unsubscribe = motionValue.on("change", (latest) => {
      setDisplayValue(Math.round(latest));
    });

    const controls = animate(motionValue, finalValue, {
      duration: 1.5,
      delay: delay,
      ease: "easeOut"
    });

    return () => {
      unsubscribe();
      controls.stop();
    };
  }, [finalValue, delay]);

  return (
    <div>
      <div className="flex justify-between text-xs font-medium text-gray-600 mb-2">
        <span>{label}</span>
        <motion.span
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3, delay: delay }}
        >
          {displayValue}%
        </motion.span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2 mb-5 overflow-hidden">
        <motion.div
          className={`h-2 rounded-full ${color}`}
          initial={{ width: 0 }}
          animate={{ width: `${finalValue}%` }}
          transition={{ 
            duration: 1.5, 
            delay: delay,
            ease: "easeOut"
          }}
        />
      </div>
    </div>
  );
}